<script setup lang="ts">
import { computed } from 'vue';
import type { CostLine } from '@/types/constructible';
import { compactNumber } from '@/composables/useConstructibleCurve';
import { useResourceSummaryStore } from '@/stores/resourceSummary.store';

// Résolution du NOM lisible d'une ressource (pour le survol enrichi) : libellé
// porté par la ligne sinon catalogue ressources du joueur (store /me) sinon code.
const rs = useResourceSummaryStore();
const labelByCode = computed<Record<string, string>>(() => {
  const m: Record<string, string> = {};
  for (const r of rs.resources) m[r.code] = r.label;
  return m;
});
function nameOf(l: CostLine): string {
  return l.label || labelByCode.value[l.code] || l.code;
}

/**
 * Liste PARTAGÉE de coûts en ressources (bâtiments / vaisseaux / recherche).
 * Icône + montant requis ; rouge si insuffisant. Le flouz est une ligne comme
 * une autre (code 'flouz') — le PARENT décide de l'inclure (recherche) ou non
 * (bâtiments/vaisseaux : Devise = solde, suivi ailleurs).
 *
 *  - mode 'required'  : quantité requise SEULE (stocks connus via la topbar).
 *  - mode 'have-need' : « dispo / requis » (modale de gestion).
 *  - compact          : chips plus petits (cartes).
 *  - max              : plafonne le nombre de chips, surplus en « +X ».
 */
const props = withDefaults(defineProps<{
  lines: CostLine[];
  mode?: 'required' | 'have-need';
  compact?: boolean;
  max?: number;
}>(), { mode: 'required', compact: false, max: 0 });

function short(l: CostLine): boolean {
  return l.insufficient ?? (l.available !== undefined && l.available < l.amount);
}
const visible = computed(() => (props.max > 0 ? props.lines.slice(0, props.max) : props.lines));
const extra = computed(() => (props.max > 0 ? props.lines.slice(props.max) : []));
</script>

<template>
  <div class="rcl" :class="{ 'rcl-compact': compact }">
    <el-tooltip
      v-for="l in visible"
      :key="l.code"
      placement="top"
      :show-after="120"
      effect="dark"
    >
      <template #content>
        <div class="rcl-tt">
          <img
            class="rcl-tt-img"
            :src="`/images/thumb/64/resources/${l.code}.png`"
            :alt="l.code"
            v-img-hide-on-error
          />
          <span class="rcl-tt-name">{{ nameOf(l) }}</span>
        </div>
      </template>
      <span
        class="rcl-chip"
        :class="{ insufficient: short(l) }"
      >
        <img
          class="rcl-icon"
          :src="`/images/thumb/32/resources/${l.code}.png`"
          :alt="l.code"
          loading="lazy"
          v-img-hide-on-error
        />
        <span class="text-mono">
          <template v-if="mode === 'have-need'">{{ compactNumber(l.available ?? 0) }}/{{ compactNumber(l.amount) }}</template>
          <template v-else>{{ compactNumber(l.amount) }}</template>
        </span>
      </span>
    </el-tooltip>
    <span
      v-if="extra.length"
      class="rcl-chip rcl-more"
      :title="extra.map(e => `${e.label || e.code} : ${e.amount}`).join('\n')"
    >+{{ extra.length }}</span>
  </div>
</template>

<style lang="scss" scoped>
.rcl {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.rcl-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.3;
  background: rgba($text-muted, 0.12);
  color: $text-secondary;
  border: 1px solid rgba($text-muted, 0.3);

  &.insufficient {
    background: rgba($color-danger, 0.15);
    color: $color-danger;
    border-color: rgba($color-danger, 0.45);
  }
}
.rcl-icon { width: 14px; height: 14px; object-fit: contain; flex-shrink: 0; }
.rcl-more { font-weight: 600; }

// Survol enrichi : image plus grande + nom (parité avec la barre de ressources).
.rcl-tt {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rcl-tt-img { width: 40px; height: 40px; object-fit: contain; flex-shrink: 0; }
.rcl-tt-name { font-weight: 600; }

// Variante compacte (cartes) : un peu plus petit + centré.
.rcl-compact {
  justify-content: center;
  .rcl-chip { font-size: 10px; padding: 1px 4px; }
}
</style>
